import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { listPatientRecords, createPatientWithRecord, searchPatients, deletePatientRecord } from '../lib/api';
import { createAndOpenEvolutionNote } from '../lib/clinicalEncounterNav';
import { computeClinicalAlerts } from '../lib/clinicalIntakeSchema';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { FileText, FolderOpen, Plus, Search, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { isSuperAdmin } from '../lib/adminRoles';

function phoneDigits10(raw) {
  const d = String(raw || '').replace(/\D/g, '');
  return d.length >= 10 ? d.slice(-10) : '';
}

export default function AdminPatientRecords() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creatingNoteId, setCreatingNoteId] = useState(null);
  const [deletingRecordId, setDeletingRecordId] = useState(null);
  const [q, setQ] = useState('');
  const [searchMode, setSearchMode] = useState('name');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [creating, setCreating] = useState(false);

  const loadByName = async (query = q) => {
    setLoading(true);
    try {
      const data = await listPatientRecords(query);
      setRecords(data.records || []);
    } catch {
      toast.error('No se pudieron cargar los expedientes');
    } finally {
      setLoading(false);
    }
  };

  const loadByPhone = async (phoneRaw) => {
    const digits = phoneDigits10(phoneRaw);
    if (digits.length < 10) {
      toast.error('Ingrese al menos 10 dígitos del teléfono');
      return;
    }
    setLoading(true);
    try {
      const data = await searchPatients({ phone: phoneRaw });
      const rows = (data.results || [])
        .filter((r) => r.patient_record_id)
        .map((r) => ({
          id: r.patient_record_id,
          status: r.record_status || 'draft',
          patient: r.patient,
          intake_adult: {},
        }));
      if (rows.length === 0) {
        toast.info('No hay expedientes con ese teléfono');
      } else if (rows.length > 1) {
        toast.info(`${rows.length} expedientes comparten este teléfono`);
      }
      setRecords(rows);
    } catch {
      toast.error('No se pudo buscar por teléfono');
    } finally {
      setLoading(false);
    }
  };

  const load = async () => {
    if (searchMode === 'phone') {
      await loadByPhone(q);
    } else {
      await loadByName(q);
    }
  };

  useEffect(() => {
    loadByName('');
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    load();
  };

  const handleNewEvolutionNote = async (recordId) => {
    setCreatingNoteId(recordId);
    try {
      const ok = await createAndOpenEvolutionNote(recordId, navigate);
      if (!ok) toast.error('No se pudo crear la nota de evolución');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo crear la nota de evolución');
    } finally {
      setCreatingNoteId(null);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (newName.trim().length < 3) {
      toast.error('Indica el nombre completo del paciente');
      return;
    }
    setCreating(true);
    try {
      const data = await createPatientWithRecord({
        full_name: newName.trim(),
        phone: newPhone.trim(),
      });
      toast.success('Expediente creado');
      setDialogOpen(false);
      setNewName('');
      setNewPhone('');
      const rid = data?.patient_record?.id;
      if (rid) {
        window.location.href = `/admin/expedientes/${rid}`;
      } else {
        load();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo crear');
    } finally {
      setCreating(false);
    }
  };

  const canDeleteRecords = isSuperAdmin(user?.role);

  const handleDeleteRecord = async (rec) => {
    const rid = String(rec?.id || '').trim();
    if (!rid) return;
    const fullName = String(rec?.patient?.full_name || 'este expediente').trim();
    const confirmed = window.confirm(
      `Se borrará definitivamente el expediente de ${fullName}, incluyendo notas evolutivas y adjuntos. Esta acción no se puede deshacer.\n\n¿Deseas continuar?`
    );
    if (!confirmed) return;
    setDeletingRecordId(rid);
    try {
      await deletePatientRecord(rid);
      toast.success('Expediente borrado definitivamente');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo borrar el expediente');
    } finally {
      setDeletingRecordId(null);
    }
  };

  return (
    <div data-testid="admin-patient-records">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold">Expedientes</h1>
          <p className="text-muted-foreground">
            Historias clínicas por paciente. Un mismo teléfono puede tener varios expedientes (familia).
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full gap-2">
              <Plus className="w-4 h-4" />
              Nuevo expediente
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nuevo paciente y expediente</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <Label>Nombre completo *</Label>
                <Input className="mt-1 h-11" value={newName} onChange={(e) => setNewName(e.target.value)} required />
              </div>
              <div>
                <Label>Teléfono</Label>
                <Input className="mt-1 h-11" value={newPhone} onChange={(e) => setNewPhone(e.target.value)} />
              </div>
              <Button type="submit" className="w-full rounded-full" disabled={creating}>
                {creating ? 'Creando…' : 'Crear'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <form onSubmit={handleSearch} className="flex flex-wrap gap-2 mb-6 max-w-xl">
        <div className="flex rounded-lg border overflow-hidden shrink-0">
          <button
            type="button"
            className={`px-3 text-sm h-11 ${searchMode === 'name' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}
            onClick={() => setSearchMode('name')}
          >
            Nombre
          </button>
          <button
            type="button"
            className={`px-3 text-sm h-11 ${searchMode === 'phone' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}
            onClick={() => setSearchMode('phone')}
          >
            Teléfono
          </button>
        </div>
        <Input
          placeholder={searchMode === 'phone' ? 'Buscar por teléfono (10 dígitos)…' : 'Buscar por nombre…'}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="h-11 flex-1 min-w-[12rem]"
        />
        <Button type="submit" variant="outline" className="h-11 px-4">
          <Search className="w-4 h-4" />
        </Button>
      </form>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <p className="p-8 text-center text-muted-foreground">Cargando…</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Paciente</TableHead>
                  <TableHead>Teléfono</TableHead>
                  <TableHead>Estado historia</TableHead>
                  <TableHead>Alertas</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {records.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      No hay expedientes
                    </TableCell>
                  </TableRow>
                ) : (
                  records.map((rec) => (
                    <TableRow key={rec.id}>
                      <TableCell className="font-medium">
                        {rec.patient?.full_name || '—'}
                      </TableCell>
                      <TableCell>{rec.patient?.phone || '—'}</TableCell>
                      <TableCell>
                        <span
                          className={`text-xs px-2 py-1 rounded-full ${
                            rec.status === 'complete'
                              ? 'bg-success/10 text-success'
                              : 'bg-muted text-muted-foreground'
                          }`}
                        >
                          {rec.status === 'complete' ? 'Historia completa' : 'Borrador'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {computeClinicalAlerts(rec.intake_adult).map((a) => (
                            <Badge key={a.id} variant="outline" className="text-amber-800 border-amber-300">
                              {a.label}
                            </Badge>
                          ))}
                          {computeClinicalAlerts(rec.intake_adult).length === 0 ? (
                            <span className="text-muted-foreground text-xs">—</span>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex flex-wrap justify-end gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            className="gap-1"
                            disabled={creatingNoteId === rec.id}
                            onClick={() => handleNewEvolutionNote(rec.id)}
                          >
                            <FileText className="w-4 h-4" />
                            Nueva nota evolutiva
                          </Button>
                          <Button asChild size="sm" variant="outline" className="gap-1">
                            <Link to={`/admin/expedientes/${rec.id}`}>
                              <FolderOpen className="w-4 h-4" />
                              Abrir
                            </Link>
                          </Button>
                          {canDeleteRecords ? (
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              disabled={deletingRecordId === rec.id}
                              onClick={() => handleDeleteRecord(rec)}
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              {deletingRecordId === rec.id ? 'Borrando…' : 'Borrar'}
                            </Button>
                          ) : null}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
